import { EApplianceEstimateCode } from "@/domains/site/site.types.ts";
import { ESiteSubOwnerStatusType } from "@/types/backoffice/dto/reference/ref-site-sub-owner-status-type.ts";
import {
  ESiteInstallationFlowStatus,
  ESiteInstallationFlowType,
} from "@/types/backoffice/dto/site/site-installation-flow-dto.ts";
import { ESiteSearchField, ESiteSearchFilterType } from "@/types/backoffice/dto/site/site-search-dto.ts";

const siteLocale = {
  site: {
    addressQuickSuggestions: "Suggestions rapides",
    updatePostalAndGeolocalizedAddress: "Mettre à jour l'adresse postale et geócodée",
    useGeolocalizedAddressAsPostalAddress: "Utiliser l'adresse géocodée comme adresse postale",
    updateAddress: "Mettre à jour l'adresse",
    updateBookedAddress: "Le site sur lequel vous souhaitez changer l'adresse a déjà un rdv planifié !",
    updateBookedAddressWarning:
      "Le RDV pris a été validé avec la précédente adresse, si vous avez modifié le code postal ou la ville, il est nécessaire de replanifier le RDV.",
    updateBookedAddressValidate: "J'ai bien compris",
    addressNotFound: "Adresse introuvable ?",
    exactAddressFound: "L'adresse exacte a été trouvée ?",
    geolocalizedAddress: "Adresse géocodée",
    postalAddress: "Adresse postale",
    nearAddress: "Adresse géocodée la plus proche",
    incompleteAddress: "Adresse incomplète",
    differentNearAddress: "Adresse géocodée différente",
    noGeolocalizedAddress: "Aucune adresse géolocalisée n'est présente",
    showNearAddress: "Voir l'adresse géocodée la plus proche",
    hideNearAddress: "Cacher l'adresse géocodée la plus proche",
    searchTitle: "Recherche de site",
    searchSiteModalLabel: "Rechercher par site ID, subscriber ID, nom, téléphone ou mail...",
    reachedMaxInterventionLimitWarning:
      'Vous avez atteint la limite de dossiers ouverts. Sélectionnez un dossier dans la liste et cliquez sur "fin d\'appel"',
    installationFlow: {
      flow: {
        [ESiteInstallationFlowType.SALE]: "Vente",
        [ESiteInstallationFlowType.PROVISION]: "Mise à disposition",
      },
      status: {
        [ESiteInstallationFlowStatus.DONE]: "Fait",
        [ESiteInstallationFlowStatus.ONGOING]: "En cours",
      },
    },
    updateOperation: "Mettre à jour l'operation",
    updateSite: "Mettre à jour le site",
    confirmRotation: "Confirmer le déménagement",
    sendSensors: "Envoyer les capteurs",
    sendGateway: "Envoyer un pilote",
    site: "Site",
    edit: "Mise à jour du site",
    siteId: "ID Site",
    noRefMission: "Sans mission de phoning",
    siteLayout: "Sous-type de site",
    siteLayouts: {
      siteLayoutStudio: "F1",
      siteLayoutOneBedroom: "F2",
      siteLayoutTwoBedroom: "F3",
      siteLayoutThreeOrMoreBedroom: "F4+",
      siteLayoutNotSpecified: "Non renseigné",
      siteLayoutFactoryOrNonStandardEquipment: "Usine / équipements exotiques",
      siteLayoutMunicipalBuildings: "Bâtiments communaux",
      siteLayoutOther: "Autre",
      siteLayoutEducation: "Établissement d'enseignement",
      siteLayoutHolidayAccommodation: "Résidence de vacances",
      siteLayoutResidentialAccommodation: "Résidence d'hébergement",
      siteLayoutHousingCollectiveInstallation: "Logement : installation collective",
      siteLayoutHotel: "Hôtellerie",
      siteLayoutRestaurant: "Restauration",
      siteLayoutFoodRetail: "Distribution alimentaire",
      siteLayoutGeneralRetail: "Distribution spécialisée",
      siteLayoutAgency: "Agence",
      siteLayoutOffice: "Bureaux",
    },
    siteTypeLayout: "Type de site",
    siteTypeLayouts: {
      siteTypeDwellingHouse: "Logement - Pavillon",
      siteTypeDwellingApartment: "Logement - Appartement",
      siteTypeProfessional: "Installation professionnelle",
      siteTypeDwellingNotSpecified: "Logement - Non renseigné",
      siteTypeAccommodationHouse: "Hébergement - Pavillon",
      siteTypeAccommodationApartment: "Hébergement - Appartement",
      siteTypeAccommodationNotSpecified: "Hébergement - Non renseigné",
    },
    create: "Créer un site",
    search: "Rechercher des sites",
    siteType: "Type de site",
    siteTypes: {
      residential: "Particulier",
      tertiary: "Tertiaire",
    },
    filterTypes: {
      [ESiteSearchFilterType.EQUALS]: "Est...",
      [ESiteSearchFilterType.CONTAINS]: "Contient...",
      [ESiteSearchFilterType.STARTS_BY]: "Démarre par...",
      [ESiteSearchFilterType.ENDS_BY]: "Fini par...",
    },
    fields: {
      [ESiteSearchField.SUBSCRIBER_ID]: "ID Adhérent",
      [ESiteSearchField.SUBSCRIBER_LASTNAME]: "Nom",
      [ESiteSearchField.SUBSCRIBER_FIRSTNAME]: "Prénom",
      [ESiteSearchField.EMAIL]: "Email",
      [ESiteSearchField.PHONE]: "Numéro de téléphone",
      [ESiteSearchField.COMPANY_NAME]: "Nom d'entreprise",
      [ESiteSearchField.SITE_ID]: "ID Site",
      [ESiteSearchField.EXTERN_ID]: "ID Externe",
      [ESiteSearchField.STREET]: "Adresse",
      [ESiteSearchField.POSTAL]: "Code postal",
      [ESiteSearchField.TOWN]: "Ville",
      [ESiteSearchField.COUNTRY]: "Pays",
      [ESiteSearchField.DELIVERY_POINT]: "Point de livraison",
      [ESiteSearchField.PILOT_HW_ID]: "Pilote - Hw ID",
      [ESiteSearchField.PILOT_CS_ID]: "Pilote - Cs ID",
      [ESiteSearchField.MOD_SERIAL]: "Modulateur - Numéro de série",
      [ESiteSearchField.MOD_HW_ID]: "Modulateur - Hw ID",
      [ESiteSearchField.MOD_CS_ID]: "Modulateur - Cs ID",
      [ESiteSearchField.MOD_NEURON_ID]: "Modulateur - Neuron ID",
    },
    additionnalInfoCategory: "Informations complémentaires",
    technicalInfoCategory: "Informations techniques",
    siteTypeCategory: "Type de site",
    partnerCategory: "Partenariat",
    siteCoordinatesCategory: "Coordonnées du site",
    companyCategory: "Informations de l'entreprise",
    generalCategory: "Informations générales",
    externalId: "Identifiant externe",
    companyName: "Nom commercial de l'entreprise",
    corporateName: "Dénomination sociale de l'entreprise",
    address: "Adresse",
    addressComplement: "Complément d'adresse",
    digicode: "Code d'accès",
    heatPumpBrand: "Marque de la pompe à chaleur",
    heatPumpModel: "Modèle de la pompe à chaleur",
    addOrDeleteOtherDeviceType: "Ajouter/supprimer un autre type d'appareil (PAC, ...)",
    stairway: "Escalier",
    level: "Étage",
    zipCode: "Code postal",
    emptyAddressWarning: "Nous n'avons pas trouvé l'adresse enregistrée, merci de revalider l'adresse géolocalisée",
    partnerAddressEmptyBtnLabel: "confirmer l'adresse de l'adhérent",
    heatPumpNotEligibleWarning: "Le modèle de pac renseigné n'est pas éligible au rdv",
    heatPumpToQualifyWarning:
      "Marque et modèle de la pompe à chaleur non renseignés : complétez-les via « Mettre à jour » dans Appareils, sans quoi le RDV sera annulé.",
    heatPumpToFillPlaceholder: "À renseigner",
    city: "Ville",
    building: "Bâtiment",
    operation: "Opération",
    mission: "Mission",
    layout: "Type de site",
    networkManager: "Régie",
    subscribedPowerType: "Puissance souscrite",
    applianceEstimatesNumberInput: {
      [EApplianceEstimateCode.HEATER]: "Nombre de radiateurs",
      [EApplianceEstimateCode.WATER_HEATER]: "Nombre de chauffe-eau",
      [EApplianceEstimateCode.HEAT_PUMP]: "Nombre de pompes à chaleur",
    },
    eligibleApplianceNumber: "Radiateurs et sèches-serviettes",
    heatPumpInSite: "Y-a-t'il une pompe à chaleur présente sur le site ?",
    occupancyType: "Type d'occupation",
    occupancyTypes: {
      [ESiteSubOwnerStatusType.OTHER]: "Autre",
      [ESiteSubOwnerStatusType.TENANT]: "Locataire",
      [ESiteSubOwnerStatusType.OWNER]: "Propriétaire",
      [ESiteSubOwnerStatusType.UNDEFINED]: "Inconnu",
    },
    surface: "Surface chauffée (en m²)",
    buildingMoreThan2Years: "Bâtiment résidentiel existant depuis plus de 2 ans ?",
    wasEquippedWithTiko: "Ancien adhérent Tiko/Engie - Mon Pilotage Elec ?",
    fillYearMonthOldestHeater:
      "Merci de renseigner approximativement le mois et année de fabrication/pose du plus vieux radiateur",
    heaterInstallYear: "Année",
    heaterInstallMonth: "Mois",
    heatingSystemOlderThan2Years: "Système de chauffage existant depuis plus de 2 ans ?",
    errorHeaterInstallDate:
      "La date renseignée a plus de 2 ans d'ancienneté, votre réponse à la question est donc incohérente. Merci de corriger votre réponse ou corriger la date renseignée",
    heatPumpDoesNotExistWarning: "Cette référence n'existe pas encore dans notre base de données, veuillez l'ajouter",
    technicianInstructions: "Consignes pour le technicien",
    modifyTechnicianInstructions: "Modifier les consignes",
    noOperationWarningLabel: "Le site n'a pas d'opération attaché, merci de ",
    noOperationWarningButton: "mettre à jour l'opération du site",
    details: {
      mandatoryInstallation: "Installation obligatoire",
    },
    deactivatedState: {
      withMyv: "Désactivé avec MyV",
      withoutMyv: "Désactivé sans MyV",
    },
  },
};

export type SiteLocale = typeof siteLocale;
export default siteLocale;
